/**
 * Document title management via React context.
 *
 * Wrap your app with <DocumentTitleProvider>, then anywhere inside:
 *
 *   const { setTitle, setUnreadIndicator } = useDocumentTitleContext();
 *   setTitle('General');           // sets page title
 *   setUnreadIndicator('3');       // shows "(3) General" in tab
 *   setUnreadIndicator('•');       // shows "(•) General" for unread without count
 *   setUnreadIndicator('');        // clears the indicator
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useDocumentTitle } from '@uidotdev/usehooks';

import { getMetaContent } from '../lib/meta';

const DocumentTitleContext = createContext(null);

// custom hook to update all favicon links (supports dark mode)
const useFavicons = (defaultLight, defaultDark, unreadLight, unreadDark, hasUnread) => {
  useEffect(() => {
    // find all favicon link elements
    const faviconLinks = Array.from(document.querySelectorAll('link[rel="icon"]'));
    if (faviconLinks.length === 0) return;

    const lightFavicon = hasUnread ? unreadLight : defaultLight;
    const darkFavicon = hasUnread ? unreadDark : defaultDark;

    faviconLinks.forEach((link) => {
      const media = link.getAttribute('media');
      if (media === '(prefers-color-scheme: dark)') {
        link.href = darkFavicon;
      } else {
        // default and light mode use the same favicon
        link.href = lightFavicon;
      }
    });
  }, [defaultLight, defaultDark, unreadLight, unreadDark, hasUnread]);
};

export const DocumentTitleProvider = ({ children }) => {
  const defaultLight = getMetaContent('favicon:default-light');
  const defaultDark = getMetaContent('favicon:default-dark');
  const unreadLight = getMetaContent('favicon:unread-light');
  const unreadDark = getMetaContent('favicon:unread-dark');

  const [pageTitle, setPageTitle] = useState(() => document.title.split(getMetaContent('title_separator')).shift());
  const [unreadIndicator, setUnreadIndicator] = useState('');

  const hasUnread = Boolean(unreadIndicator);
  useFavicons(defaultLight, defaultDark, unreadLight, unreadDark, hasUnread);

  useDocumentTitle(
    [unreadIndicator ? `(${unreadIndicator}) ${pageTitle}` : pageTitle, getMetaContent('title_suffix')]
      .filter(Boolean)
      .join(` ${getMetaContent('title_separator')} `),
  );

  const value = useMemo(
    () => ({
      title: pageTitle,
      setTitle: setPageTitle,
      setUnreadIndicator,
    }),
    [pageTitle],
  );

  return <DocumentTitleContext.Provider value={value}>{children}</DocumentTitleContext.Provider>;
};

export const useDocumentTitleContext = () => {
  const context = useContext(DocumentTitleContext);
  if (!context) {
    throw new Error('useDocumentTitleContext must be used within DocumentTitleProvider');
  }
  return context;
};
